import {
  REPORT_DEFINITIONS,
  type ReportColumn,
  type ReportId,
  type ReportResponseType,
} from '@aeris/contracts'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * O relatório como arquivo — F158.
 *
 * ## O arquivo leva "Outros" e "Total"
 *
 * É a regra que a tela também segue, e vale repetir por quê: um Excel com só o
 * top 10 mente do mesmo jeito que uma tela com só o top 10. Quem baixa, soma a
 * coluna e compara com o faturamento do painel conclui que um dos dois está
 * quebrado — e a resposta certa é que o arquivo nunca disse que era o todo.
 *
 * As duas linhas vão **no fim e rotuladas**, nunca como se fossem mais um
 * cliente.
 *
 * ## As colunas vêm de `REPORT_DEFINITIONS`
 *
 * Não há uma segunda lista aqui. A tela, o arquivo e o servidor leem o mesmo
 * dado — é isso que faz uma coluna nova aparecer nos três lugares ao mesmo
 * tempo, em vez de em dois e meio.
 */

/** Uma linha do arquivo: o rótulo mais o mapa de valores. */
export type ReportExportRow = {
  readonly label: string
  readonly values: Readonly<Record<string, string | null>>
}

/**
 * Converte um valor do relatório no que o CSV e o XLSX entendem.
 *
 * Dinheiro e quantidade continuam texto decimal exato (ADR-0016) — o arquivo
 * recebe `{ number }` e o Excel lê um número de verdade, sem passar por
 * `Number` no meio do caminho.
 */
const valueOf = (
  column: ReportColumn,
  raw: string | null | undefined
): ReturnType<typeof textValue> => {
  if (raw === null || raw === undefined) return null
  switch (column.kind) {
    case 'money':
    case 'quantity':
    case 'count':
    case 'percent':
      return numberValue(raw)
    case 'date':
      return dateValue(raw)
    case 'text':
      return textValue(raw)
  }
}

export const reportExportColumns = (
  report: ReportId
): readonly ExportColumn<ReportExportRow>[] => [
  {
    id: 'label',
    label: REPORT_DEFINITIONS[report].label,
    value: (row) => textValue(row.label),
  },
  ...REPORT_DEFINITIONS[report].columns.map(
    (column): ExportColumn<ReportExportRow> => ({
      id: column.id,
      label: column.label,
      value: (row) => valueOf(column, row.values[column.id]),
    })
  ),
]

/**
 * As linhas do arquivo: o ranking, depois "Outros", depois "Total".
 *
 * `others` só vira linha quando existe — quando o corte já mostra tudo, uma
 * linha "Outros" de zeros faria a pessoa procurar o que não há.
 */
export const reportExportRows = (
  page: ReportResponseType
): readonly ReportExportRow[] => [
  ...page.rows.map((row) => ({ label: row.label, values: row.values })),
  ...(page.others === null
    ? []
    : [
        {
          label: `Outros (${String(page.others.groups)})`,
          values: page.others.values,
        },
      ]),
  { label: 'Total', values: page.totals.values },
]
